/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useEffect, useState } from 'react';
import { onAuthStateChanged, User, signOut } from "firebase/auth";
import iconGoogle from './icon-google.svg';
import iconFacebook from './icon-facebook.svg';
import iconEmail from './icon-email.svg';
import './auth.css';
import { ReactSVG } from 'react-svg';
import { auth, firestoreService } from '../services/FirestoreService'
import AuthService from '../services/AuthService';
import { stripeService, SubscriptionTier } from '../services/StripeService';
import PrivacyNotice from './PrivacyNotice';
import ManageProviders from './ManagerProviders';
import SubscriptionModal from './SubscriptionModal';

// CSS styles for modal
const modalOverlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    right: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
    backgroundColor: 'white',
    padding: '5px 20px 5px',
    borderRadius: '10px',
    width: '200px',
    maxHeight: '90%',
    overflowY: 'auto',
    position: 'relative',
    textShadow: 'none',
    color: 'black',
    fontFamily: 'arial',
    textAlign: 'left',
    fontSize: 'x-small'
};

const buttonStyle: React.CSSProperties = {
    fontSize: 'small',
    height: '24px',
    width: '180px'
};
const closeButtonStyle: React.CSSProperties = {
    fontSize: 'x-small',
    height: '16px',
    borderRadius: '5px',
    background: 'lightgrey',
    padding: '2px',
    width: '16px',
    marginRight: '-14px'
};

interface AccountProps {
    onClose: () => void;
    onUserChanged: (userData: User | null) => void;
}

const Account: React.FC<AccountProps> = ({onClose, onUserChanged}) => {

    const [errors,           setErrors          ] = useState('');
    const [email,            setEmail           ] = useState('');
    const [userId,           setUserId          ] = useState('');
    const [birthday,         setBirthday        ] = useState('');
    const [acceptTerms,      setAcceptTerms     ] = useState(false);
    const [userIdState,      setUserIdState     ] = useState('');
    const [userId_User,      setUserId_User     ] = useState('');
    const [birthday_User,    setBirthday_User   ] = useState('');
    const [acceptTerms_User, setAcceptTerms_User] = useState('');
    const [subscription_User,setSubscription_User] = useState('');
    const [subExpiration_User,setSubExpiration_User] = useState('');
    const [password,         setPassword        ] = useState('');
    const [newPassword,      setNewPassword     ] = useState('');
    const [confirmPassword,  setConfirmPassword ] = useState('');
    const [user,             setUser            ] = useState<User | null>(null);
    const [emailVerified,    setEmailVerified   ] = useState<boolean>(false);
    const [hasEmailLogin,    setHasEmailLogin   ] = useState<boolean>(false);
    const [showPrivacy,      setShowPrivacy     ] = useState<boolean>(false);
    const [,                 setForceUpdate     ] = useState<number>(0);
    const [watchingUser,     setWatchingUser    ] = useState<boolean>(false);
    const [closeAccountWindowOnLogin, setCloseAccountWindowOnLogin] = useState<boolean>(false);
    const [showSubscriptionModal, setShowSubscriptionModal] = useState<boolean>(false);

    const [state, setState] = useState<'login' | 'loginEmail' | 'register' | 'addEmail' | 'changePassword' | 'connecting' | 'registering' | 'connected' | 'delete'>('login');

    useEffect(() => {
        // Watch for auth state changes
        onAuthStateChanged(auth, (user: User | null) => {
            setUser(user);
            onLogin(user);
            // If there is no user logged in, then when they do log in, we want to close the account window
            if (user == null) {
                setCloseAccountWindowOnLogin(true);
            }
            if (user && !watchingUser) {
                const watching = firestoreService.watchUserData(setUserData);
                setWatchingUser(watching);
            }
        });
    }, [])

    const setUserData = (data: Record<string, any> | null) => {
        console.log('User data changed:', data);
        if (data?.birthDate) {
            const date = data.birthDate.toDate(); // Firestore Timestamp -> JS Date
            const formattedDate = date.toISOString().split('T')[0]; // "YYYY-MM-DD"
            setBirthday(formattedDate);
            setBirthday_User(formattedDate)
        }
        if (data?.username) {
            setUserId(data.username);
            if (data.username_lower) {
                setUserId_User(data.username);
                setUserIdState('');
            }
        }
        if (data?.subscriptionExpirationDate) {
            const date = data.subscriptionExpirationDate.toDate(); // Firestore Timestamp -> JS Date
            const formattedDate = date.toISOString().split('T')[0]; // "YYYY-MM-DD"
            if (date > new Date()) {
                setSubscription_User(data.subscriptionTier);
                setSubExpiration_User(formattedDate);
            } else {
                setSubscription_User('None');
                setSubExpiration_User('Expired');
            }
        }
        if (data?.subscriptionTier) {
            setSubscription_User(data.subscriptionTier);
            if (data.subscriptionExpirationDate) {
                const date = data.subscriptionExpirationDate.toDate();
                const formattedDate = date.toISOString().split('T')[0];
                setSubExpiration_User(formattedDate);
            }
        }
        if (data?.acceptedTerms) {
            setAcceptTerms(true);
            const date = data.acceptedTerms.toDate(); // Firestore Timestamp -> JS Date
            const formattedDate = date.toISOString().split('T')[0]; // "YYYY-MM-DD"
            setAcceptTerms_User(formattedDate);
        }
        if (closeAccountWindowOnLogin && data?.birthDate && data?.username_lower && data?.acceptedTerms &&
            (!hasEmailLogin || user?.emailVerified)) {
            console.log('Logged in, with userName, birthday, terms accepted and email verified. Closing login dialog.');
            onClose();
        }
    }

    useEffect(() => {
        setEmailVerified(user?.emailVerified || false);
        if (!userId) {
            setUserId(user?.displayName ?? '');
        }
    }, [user])

    useEffect(() => {
        if (!userId) {
            return;
        }

        if (AuthService.isValidUserId(userId)) {
            setUserIdState('Checking availability....');
            const checkAvailable = async (userId: string) => {
                const isAvailable = await firestoreService.isUsernameAvailable(userId);
                setUserIdState(isAvailable ? 'Available' : 'unavailable');
            }
            checkAvailable(userId);
        } else {
            if (userId.length < 3) {
                setUserIdState('Invalid (must be at least 3 characters)');
            } else if (userId.length > 30) {
                setUserIdState('Invalid (too long, use 30 characters or less)');
            } else {
                setUserIdState('Invalid (only letters, digits, \'-\'s or \'_\'s)');
            }
        }
    }, [userId]);


    const refresh = () => {
        setForceUpdate((prev) => prev+1);
    }

    const handleUpdateAccount = async () => {
        const birthDate = new Date(birthday);
        // Validate birthday
        if (!validateBirthday(birthDate) || !user) {
          return;
        }

        const oldState = state;
        setState('registering');
        if (await firestoreService.registerUsername(user.uid, userId, birthDate)) {
            if (!acceptTerms_User) {
                setAcceptTerms_User(new Date().toISOString().split('T')[0]);
            }
            if (!birthday_User) {
                const formattedDate = birthDate.toISOString().split('T')[0]; // "YYYY-MM-DD"
                setBirthday_User(formattedDate)
            }
            if (!userId_User) {
                setUserId_User(userId);
            }
            if (state !== 'connected') {
                setState('connected');
            }
            if (closeAccountWindowOnLogin && userId && birthday_User && acceptTerms_User && (!hasEmailLogin || user.emailVerified)) {
                console.log('Logged in, with userName, birthday, terms accepted and email verified. closing login dialog.');
                onClose();
            }
        //     setState('connected');
        // } else if (state !== oldState) {
        //     setState(oldState);
        }
        setState('connected');
    }

    const validateBirthday = (birthDate: Date) => {
      // Validate birthday
      const today = new Date();
      const minBirthDate = new Date();
      minBirthDate.setFullYear(today.getFullYear() - 13); // 13 years ago today.
      if (birthDate > minBirthDate) {
        alert("You must be at least 13 years old to create an account.");
        setErrors("You must be at least 13 years old to create an account.");
        return false;
      }
      return true;
    }
    
    const handleRegister = async () => {
      setErrors('');
      if (password !== confirmPassword) {
        setErrors("Passwords do not match.");
        return;
      }
      if (!acceptTerms) {
        setErrors("Must accept terms.");
        return;
      }
      const birthDate = new Date(birthday);
      // Validate birthday
      if (!validateBirthday(birthDate)) {
        return;
      }

      if (await AuthService.registerWithEmail(email.trim(), password, userId, birthDate, setErrors)) {
        if (!acceptTerms_User) setAcceptTerms_User(new Date().toISOString().split('T')[0]);
        if (!birthday_User) setBirthday_User(birthDate.toISOString().split('T')[0]);
        if (!userId_User) setUserId_User(userId);
      }
    };
    
    const onLogin = (newUser: User | null) => {
        if (newUser !== user) {
            if (errors) {
                setErrors('');
            }
            setUser(newUser);
        }
        onUserChanged(newUser);
        if (newUser == null) {
            clearAccount();
        } else {
            // Check if the user signed in with email/password
            const emailPassword = newUser.providerData.some((provider) => provider.providerId === "password") || false;
            if (emailPassword !== hasEmailLogin) {
                setHasEmailLogin(emailPassword);
            }
            if (emailVerified !== newUser.emailVerified) {
                setEmailVerified(newUser.emailVerified);
            }
            if (state !== 'connected') {
                console.log("User is logged in:", newUser);
                setState('connected');
            }
        }
    }

    const onAddEmailLink = () => {
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setHasEmailLogin(true);
        setState('addEmail');
    }
    const onRemoveEmailLink = () => {
        setHasEmailLogin(false);
    }

    const refreshUser = async() => {
        if (user) {
            const newUser = await AuthService.refreshUser(user, setErrors);
            if (newUser) {
                if (newUser !== user) {
                    setUser(newUser);
                }
                if (emailVerified !== newUser.emailVerified) {
                    setEmailVerified(newUser.emailVerified);
                }
            }
        }
    }

    const onLogout = async () => {
        setErrors('');
        await signOut(auth);
        setState('login');
    }
    const onConnectEmail = async () => {
        setErrors('');
        const oldState = state;
        setState('connecting');
        if (await AuthService.loginWithEmail(email, password, setErrors)) {
            setState('connected');
        } else {
            setState(oldState);
        }
    }

    // When the close button is present, and the user successfully logs in, close the popup.
    const loginComplete = () => {
        setState('connected');
    }

    const onEmailLogin = async () => {
        setErrors('');
        setState('connecting');
        const res = await AuthService.loginWithEmail(email, password, setErrors);
        if (res) {
            loginComplete();
        } else {
            setState('login');
        }
    }

    const onDeleteAccount = async () => {
        setErrors('');
        if (user && await firestoreService.deleteUserDataWithAuthentication(user, userId_User, hasEmailLogin ? confirmPassword : '', setErrors)) {
            clearAccount();
            setErrors('Account deleted.');
        }
    }

    const clearAccount = () => {
        // Reset everything back to zero:
        setUserId('');
        setUserId_User('');
        setBirthday('');
        setBirthday_User('');
        setAcceptTerms(false);
        setAcceptTerms_User('');
        setPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setUser(null);
        setEmailVerified(false);
        setHasEmailLogin(false);
        setShowPrivacy(false);
        setEmail('');
        setUserIdState('');
        setErrors('');
        setState('login');
    }

    const handleUpdatePassword = async() => {
        setErrors('');
        if (user && await AuthService.changeUserPassword(user, password, newPassword, setErrors)) {
            alert('Password changed!');
            setState('connected');
        }
    }
    
    const handleUpgrade = async() => {
        setShowSubscriptionModal(true);
    }
    
    const handleCancelSubscription = async() => {
        if (!user) return;
        
        const confirmCancel = window.confirm(
            "Are you sure you want to cancel your subscription? " + 
            "Your subscription will remain active until the end of your current billing period, " +
            "but will not renew after that."
        );
        
        if (!confirmCancel) return;
        
        try {
            const success = await stripeService.cancelSubscription(user);
            
            if (success) {
                alert("Your subscription has been canceled successfully. It will remain active until the end of your current billing period.");
                setSubscription_User('Canceling');
            } else {
                alert("There was an error canceling your subscription. Please try again or contact support.");
            }
        } catch (error) {
            console.error("Error canceling subscription:", error);
            alert("There was an error canceling your subscription. Please try again or contact support.");
        }
    }
    
    const handleCancel = async() => {
        switch (state) {
            case 'login':
                onClose();
                break;
            case 'addEmail':
                setState('connected');
                setHasEmailLogin(false);
                break;
            case 'connecting':
            case 'registering':
            case 'connected':
                // TODO: where do we go from these?
                break;
            case 'loginEmail':
                setState('login');
                break;
            case 'register':
                setState('loginEmail');
                break;
        }
    }
    
    if (showPrivacy) {
        return <PrivacyNotice onClose={() => setShowPrivacy(false)} />
    }

    if (showSubscriptionModal && user) {
        return <SubscriptionModal user={user} onClose={() => setShowSubscriptionModal(false)} />
    }

    const showLoginButtons        = state === 'login';
    const showLogInGoogle         = state === 'login';
    const showLogInFacebook       = state === 'login';
    const showLogInEmail          = state === 'login';
    const showConnectEmail        = state === 'loginEmail' || state === 'register' || state === 'addEmail';
    const showPasswordConf        = state === 'register' || state === 'addEmail';
    const showConnectEmailButton  = state === 'addEmail';// && !hasEmailLogin;
    const showLoginRegisterForgot = state === 'loginEmail';
    const showCancelLogin         = state === 'loginEmail'|| state === 'register' || state === 'addEmail';
    const showUserId              = state === 'connected' || state === 'register';
    const showBirthday            = (state === 'connected' || state === 'register') && !birthday_User;
    const showTerms               = (state === 'connected' || state === 'register') && !acceptTerms_User;
    const showCreateAccount       = state === 'register';
    const showUpdateAccount       = state === 'connected' && ((showUserId && !userId_User) || showBirthday || showTerms);
    const showManageProviders     = state === 'connected' && user && userId_User && birthday_User && acceptTerms_User;
    const showEmailNotVerified    = state === 'connected' && user && !emailVerified && hasEmailLogin && userId_User && birthday_User && acceptTerms_User;
    const showChangePassword      = state === 'connected' && hasEmailLogin && user && userId_User && birthday_User && acceptTerms_User;
    const showDeleteAccount       = state === 'connected';
    const showSubscription        = state === 'connected' && !showUpdateAccount;
    const showUpgrade             = showSubscription;

    console.log(`state: ${state}`);
    return ( <>
        <div style={modalOverlayStyle}>
            <div style={modalStyle}>
                <div style={{textAlign: 'right'}}><button style={closeButtonStyle} onClick={onClose}>X</button></div>
                <div style={{ textAlign: "center" }}>
                    {state === 'connecting' && <div style={{fontSize: 'x-large', backgroundColor: 'lightgrey', fontStyle: 'italic',  border: '2px solid grey', borderRadius: '15px'}}>Connecting...</div>}
                    {state === 'registering' && <div style={{fontSize: 'x-large', backgroundColor: 'lightgrey', fontStyle: 'italic',  border: '2px solid grey', borderRadius: '15px'}}>Registering...</div>}
                    {showLoginButtons && <h2>Login</h2>}
                    {showLogInGoogle && (
                        <button className="gsi-material-button" onClick={() => AuthService.loginWithGoogle(setErrors, loginComplete)}>
                            <div className="gsi-material-button-state"></div>
                            <div className="gsi-material-button-content-wrapper">
                                <div className="gsi-material-button-icon">
                                    <ReactSVG src={iconGoogle} />
                                </div>
                                <span className="gsi-material-button-contents">Sign in with Google</span>
                                <span style={{display: 'none'}}>Sign in with Google</span>
                            </div>
                        </button>
                    )}
                    {showLogInFacebook && (
                        <button className="gsi-material-button" onClick={() => AuthService.loginWithFacebook(setErrors, loginComplete)}>
                            <div className="gsi-material-button-state"></div>
                            <div className="gsi-material-button-content-wrapper">
                                <div className="gsi-material-button-icon">
                                    <ReactSVG src={iconFacebook} />
                                </div>
                                <span className="gsi-material-button-contents">Sign in with Facebook</span>
                                <span style={{display: 'none'}}>Sign in with Facebook</span>
                            </div>
                        </button>)
                    }
                    {showLogInEmail && (
                        <button className="gsi-material-button" onClick={() => setState('loginEmail')}>
                            <div className="gsi-material-button-state"></div>
                            <div className="gsi-material-button-content-wrapper">
                                <div className="gsi-material-button-icon">
                                    <ReactSVG src={iconEmail} />
                                </div>
                                <span className="gsi-material-button-contents">Sign in with Email</span>
                                <span style={{display: 'none'}}>Sign in with Email</span>
                            </div>
                        </button>
                    )}

                    {showConnectEmail && (<>
                        <h2>{state == 'addEmail' ? 'Connect Email' : 'Login'}</h2>
                        <div className={AuthService.isValidEmail(email) ? 'validInput' : 'invalidInput'}>
                            <div className={'label'}>Email:</div>
                            <input type="email" placeholder='Email'
                                value={email} onChange={(e) => {setEmail(e.target.value); setErrors('');}}/>
                            {!AuthService.isValidEmail(email) && <div className='errorText'>Invalid email address.</div>}
                        </div>
                    <div className={AuthService.isValidPassword(password) ? 'validInput' : 'invalidInput'}>
                            <div className={'label'}>Password:</div>
                            <input type="password" placeholder="Password" value={password} onChange={(e) => {setPassword(e.target.value); setErrors('');}} />
                            <div className='errorText'>{AuthService.isValidPassword(password) ? ' ' : 'Must be at least 6 characters.'}</div>
                        </div>
                        {showPasswordConf && (<>
                            <div className={confirmPassword === password ? 'validInput' : 'invalidInput'}>
                                <div className={'label'}>Confirm Password:</div>
                                <input type="password" placeholder="Confirm Password" value={confirmPassword} 
                                       onChange={(e) => {setConfirmPassword(e.target.value); setErrors('');}} />
                                <div className='errorText'>{confirmPassword === password ? ' ' : 'Passwords do not match.'}</div>
                            </div>
                        </>)}
                    </>)}

                    {showConnectEmailButton && (
                            <button style={buttonStyle}
                                    disabled={!AuthService.isValidEmail(email) || !AuthService.isValidPassword(password) || (password !== confirmPassword)}
                                    onClick={onConnectEmail}>Connect Email</button>)}
                    {showLoginRegisterForgot && (<>
                            <button style={buttonStyle} onClick={onEmailLogin}>Login</button>
                            <button style={buttonStyle} onClick={() => {setErrors(''); setState('register');}}>Register</button>
                            <button style={buttonStyle} onClick={() => AuthService.handlePasswordReset(email, setErrors)}>Forgot Password</button>
                        </>)}
                    {showUserId && (
                        <div className={AuthService.isValidUserId(userId) ? 'validInput' : 'invalidInput'}>
                            <div className={'label'}>User ID:</div>
                            <input type="text" pattern="[a-zA-Z0-9_\-]{3,30}" disabled={!!userId_User} placeholder="User ID" value={userId} onChange={(e) => setUserId(e.target.value)}/>
                            {!userId_User && userIdState === 'Available' && <div style={{color: 'green'}}>{userIdState}</div>}
                            {!userId_User && userIdState !== 'Available' && <div className='errorText'>{userIdState}</div>}
                        </div>
                    )}
                    {showBirthday && (
                        <div className={AuthService.isValidUserId(userId) ? 'validInput' : 'invalidInput'}>
                            <div className={'label'}>Birth date:</div>
                            <input type="date" value={birthday} onChange={(e) => setBirthday(e.target.value)} required />
                            <div className='errorText'>{birthday !== '' ? ' ' : 'Enter your birthday.'}</div>
                            </div>
                    )}
                    {showTerms && (<>
                        <input type="checkbox" id="terms" name="terms" required checked={acceptTerms} onChange={() => setAcceptTerms((prev) => !prev)}/>
                        <label htmlFor="terms">
                            I agree to the <a href="#" onClick={() => setShowPrivacy(true)}>Privacy Policy</a>.
                        </label>
                    </>)}
                    {showCreateAccount && (
                        <button style={buttonStyle} onClick={() => handleRegister()} 
                                disabled={(!acceptTerms && !acceptTerms_User) || 
                                    !AuthService.isValidPassword(password) ||
                                    !AuthService.isValidUserId(userId) ||
                                    birthday == '' ||
                                    (confirmPassword !== password)}
                                >Create Account</button>)}
                    {showUpdateAccount && (
                        <button style={buttonStyle} onClick={() => handleUpdateAccount()} 
                                disabled={(!acceptTerms && !acceptTerms_User) || 
                                    !AuthService.isValidUserId(userId) ||
                                    birthday == '' ||
                                    (showPasswordConf && confirmPassword !== password)}
                                >Update Account</button>)}

                    {showCancelLogin && (<>
                        <button style={buttonStyle} onClick={handleCancel}>Cancel</button>
                    </>)}

                    {user && (<>
                        {!showUserId && (<>
                            <h2>Account</h2>
                            <b>{user?.displayName}</b>
                        </>)}
                        <br/>
                        {showSubscription && (<>
                            <br/>
                            <table>
                                <tbody>
                                    <tr><td style={{textAlign: 'right'}}>Subscription Type:</td><th>{subscription_User}</th></tr>
                                    <tr><td style={{textAlign: 'right'}}>Expiration Date:</td><th>{subExpiration_User}</th></tr>
                                </tbody>
                            </table>
                            
                            {showUpgrade && (
                                (subscription_User === 'None' || subscription_User === SubscriptionTier.APPRENTICE) ? (
                                    <button style={buttonStyle} onClick={handleUpgrade}>Upgrade</button>
                                ) : (
                                    <>
                                        <button style={buttonStyle} onClick={handleUpgrade}>Change Plan</button>
                                        <button style={{...buttonStyle, backgroundColor: '#ff4d4d', marginTop: '5px'}} 
                                                onClick={handleCancelSubscription}>
                                            Cancel Subscription
                                        </button>
                                    </>
                                )
                            )}
                            <br/>
                        </>)}                        
                        <br/>
                        {showManageProviders && <ManageProviders user={user} onUpdate={refresh} onAddEmailLink={onAddEmailLink} onRemoveEmailLink={onRemoveEmailLink}/>}
                        <br/>
                        <br/>
                        <div style={{textAlign: 'left'}}>
                            {showEmailNotVerified && (<>
                                <div style={{textAlign: 'left'}}>
                                    Your email address (<b>{user.email || ''}</b>) has not been verified. Please check your email and click on the verification link.<br/><br/>
                                    <div style={{ textAlign: "center" }}>
                                    <button style={buttonStyle} onClick={() => AuthService.resendConfirmationEmail(user, setErrors)}>Resend verification</button>
                                    <button style={buttonStyle} onClick={refreshUser}>Re-check</button>
                                    </div>
                                </div>
                            </>)}

                            <div style={{ textAlign: "center" }}>
                                {state !== 'changePassword' && state !== 'delete' && (
                                    <button style={buttonStyle} disabled={!showChangePassword} 
                                            onClick={() => {setConfirmPassword(''); setNewPassword(''); setPassword(''); setState('changePassword')}}>Change Password</button>
                                )}
                                {state === 'changePassword' && (<>
                                    <h2>Change Password</h2>
                                    for email <b>{user.email}</b><br/><br/>
                                    <div className={'label'}>Current Password:</div>
                                    <input type="password" placeholder="Current Password" value={password} onChange={(e) => setPassword(e.target.value)} />

                                    <div className={AuthService.isValidPassword(newPassword) ? 'validInput' : 'invalidInput'}>
                                        <div className={'label'}>New Password:</div>
                                        <input type="password" placeholder="New Password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                                        <div className='errorText'>{AuthService.isValidPassword(newPassword) ? ' ' : 'Must be at least 6 characters.'}</div>
                                    </div>
                                    <div className={confirmPassword === newPassword ? 'validInput' : 'invalidInput'}>
                                        <div className={'label'}>Confirm New Password:</div>
                                        <input type="password" placeholder="Confirm New Password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
                                        <div className='errorText'>{confirmPassword === newPassword ? ' ' : 'New passwords do not match.'}</div>
                                    </div>
                                    <button style={buttonStyle}
                                            onClick={handleUpdatePassword}
                                            disabled={!AuthService.isValidPassword(password) || !newPassword || (confirmPassword !== newPassword)}>Update Password</button>
                                    <button style={buttonStyle} onClick={() => setState('connected')}>Cancel</button>
                                </>)}
                                
                                {state !== 'delete' && state !== 'changePassword' && <button style={buttonStyle} disabled={!showDeleteAccount} onClick={() => {setConfirmPassword(''); setState('delete');}}>Delete Account</button>}
                                {state === 'delete' && user && (<>
                                    <h2>Delete Account</h2>
                                    {hasEmailLogin && (<>
                                        <div className={'label'}>Re-enter Password:</div>
                                        <input type="password" placeholder="Password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} /><br/>
                                    </>)}
                                    <div style={{color: 'red'}}>
                                        Deleting an account will delete all data associated with that account, and can not be undone, please be sure this is what you want to do!
                                    </div><br/>
                                    <button style={buttonStyle} onClick={onDeleteAccount}
                                            disabled={hasEmailLogin && !AuthService.isValidPassword(confirmPassword)}>Confirm Delete</button>
                                    <button style={buttonStyle} onClick={() => setState('connected')}>Cancel</button>
                                </>)}
                                {state ==='connected' && <button style={buttonStyle} disabled={state !=='connected'} onClick={onLogout}>Logout</button>}
                            </div>
                        </div>
                    </>)}
                    <div className='errorText' style={{fontSize: 'larger'}}>{errors}</div>
                </div>
                <div style={{textAlign: 'right'}}>
                    <br/>
                    <a href="#" onClick={() => setShowPrivacy(true)}>Privacy Policy</a><br/><br/>
                </div>
            </div>
        </div>
    </>
    );
}
export default Account;